"use client";

import { c as _c } from "react-compiler-runtime";
import { AlertCircle, Camera, X } from "lucide-react";
import NextImage from "next/image";
import { useRef, useState } from "react";
import { Text } from "@/components/primitives";
type MediaUploaderProps = {
  readonly acceptedTypes?: string[];
  readonly maxFileSize?: number;
  readonly onFileSelected: (file: File) => void;
  readonly onReset?: () => void;
  readonly thumbnailUrl?: string | null;
  readonly status?: MediaUploaderStatus;
  readonly errorMessage?: string | null;
  readonly validationIssues?: readonly string[];
  readonly disabled?: boolean;
  readonly hints?: string[];
  readonly promptText?: string;
  readonly dropActiveText?: string;
  readonly icon?: React.ReactNode;
};
export function MediaUploader(t0) {
  const $ = _c(57);
  const {
    acceptedTypes: t1,
    maxFileSize: t2,
    onFileSelected,
    onReset,
    thumbnailUrl,
    status: t3,
    errorMessage,
    validationIssues,
    disabled: t4,
    hints,
    promptText,
    dropActiveText,
    icon
  } = t0;
  const acceptedTypes = t1 === undefined ? DEFAULT_ACCEPTED_TYPES : t1;
  const maxFileSize = t2 === undefined ? DEFAULT_MAX_FILE_SIZE : t2;
  const status = t3 === undefined ? "idle" : t3;
  const disabled = t4 === undefined ? false : t4;
  const [isDragging, setIsDragging] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const fileInputRef = useRef(null);
  const resolvedError = status === "error" ? errorMessage : internalError;
  const showError = !!resolvedError;
  let t5;
  if ($[0] !== acceptedTypes || $[1] !== maxFileSize) {
    t5 = file => {
      if (!acceptedTypes.includes(file.type) && !file.type.startsWith("image/")) {
        return "File type is not supported.";
      }
      if (file.size > maxFileSize) {
        return `File is too large. Maximum size is ${Math.round(maxFileSize / 1048576)}MB.`;
      }
      return null;
    };
    $[0] = acceptedTypes;
    $[1] = maxFileSize;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const validateFile = t5;
  let t6;
  if ($[3] !== onFileSelected || $[4] !== validateFile) {
    t6 = file_0 => {
      setInternalError(null);
      const validationError = validateFile(file_0);
      if (validationError) {
        setInternalError(validationError);
        return;
      }
      onFileSelected(file_0);
    };
    $[3] = onFileSelected;
    $[4] = validateFile;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const processFile = t6;
  let t7;
  if ($[6] !== disabled) {
    t7 = e => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    };
    $[6] = disabled;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleDragOver = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsDragging(false);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleDragLeave = t8;
  let t9;
  if ($[9] !== disabled || $[10] !== processFile) {
    t9 = e_0 => {
      e_0.preventDefault();
      setIsDragging(false);
      if (disabled) {
        return;
      }
      const files = e_0.dataTransfer.files;
      if (files.length > 0) {
        processFile(files[0]);
      }
    };
    $[9] = disabled;
    $[10] = processFile;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleDrop = t9;
  let t10;
  if ($[12] !== processFile) {
    t10 = e_1 => {
      const file_1 = e_1.target.files?.[0];
      if (file_1) {
        processFile(file_1);
      }
      e_1.target.value = "";
    };
    $[12] = processFile;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const handleInputChange = t10;
  let t11;
  if ($[14] !== disabled) {
    t11 = () => {
      if (!disabled) {
        fileInputRef.current?.click();
      }
    };
    $[14] = disabled;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const openFilePicker = t11;
  let t12;
  if ($[16] !== onReset) {
    t12 = () => {
      setInternalError(null);
      onReset?.();
    };
    $[16] = onReset;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const handleReset = t12;
  let t13;
  if ($[18] !== disabled || $[19] !== dropActiveText || $[20] !== handleDragOver || $[21] !== handleDrop || $[22] !== hints || $[23] !== icon || $[24] !== isDragging || $[25] !== openFilePicker || $[26] !== promptText) {
    t13 = () => <div className={`overflow-hidden rounded-xl border border-[#ece8e2] bg-[#fdfcfa] transition-colors ${isDragging ? "border-primary/70 bg-primary/2" : ""} ${disabled ? "pointer-events-none opacity-50" : ""}`}><button className="flex min-h-[170px] w-full cursor-pointer flex-col items-center justify-center gap-3 px-6 py-8 text-center transition-colors hover:bg-[#f8f5f0]/35" disabled={disabled} type="button" onClick={openFilePicker} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}>{icon ?? <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#f1eee8]"><Camera className="h-4 w-4 text-[#6f6d6a]" /></div>}<Text component="p" text={isDragging ? dropActiveText ?? "Drop photo here" : promptText ?? "Tap to upload"} variant="body-sm" weight="medium" /></button>{hints && hints.length > 0 && <div className="border-t border-[#ebe7e1] px-5 py-4"><ul className="list-none space-y-1.5 text-left">{hints.map(_temp)}</ul></div>}</div>;
    $[18] = disabled;
    $[19] = dropActiveText;
    $[20] = handleDragOver;
    $[21] = handleDrop;
    $[22] = hints;
    $[23] = icon;
    $[24] = isDragging;
    $[25] = openFilePicker;
    $[26] = promptText;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const renderDropzone = t13;
  let t14;
  if ($[28] !== handleReset || $[29] !== onReset || $[30] !== thumbnailUrl) {
    t14 = () => {
      if (!thumbnailUrl) {
        return null;
      }
      return <div className="relative w-full overflow-hidden rounded-2xl"><div className="relative aspect-3/4 w-full"><NextImage fill={true} unoptimized={true} alt="Uploaded media" className="object-cover" src={thumbnailUrl} /></div>{onReset && <button className="bg-background/80 hover:bg-background absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full shadow-lg backdrop-blur-sm transition-colors" type="button" onClick={handleReset}><X className="h-4 w-4" /></button>}</div>;
    };
    $[28] = handleReset;
    $[29] = onReset;
    $[30] = thumbnailUrl;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const renderPreview = t14;
  let t15;
  if ($[32] !== handleReset || $[33] !== onReset || $[34] !== resolvedError || $[35] !== showError || $[36] !== validationIssues) {
    t15 = () => {
      if (!showError) {
        return null;
      }
      return <div className="flex flex-col gap-4"><div className="border-destructive/15 bg-destructive/8 rounded-xl border p-4"><div className="flex items-start gap-3"><AlertCircle className="text-destructive mt-0.5 h-5 w-5 shrink-0" /><div><Text component="p" text={resolvedError} tone="destructive" variant="body-sm" weight="medium" />{validationIssues && validationIssues.length > 0 && <ul className="mt-2 space-y-1">{validationIssues.map(_temp2)}</ul>}</div></div></div>{onReset && <button className="underline-offset-2 hover:underline" type="button" onClick={handleReset}><Text component="span" text="Try a different photo" tone="primary" variant="body-sm" weight="medium" /></button>}</div>;
    };
    $[32] = handleReset;
    $[33] = onReset;
    $[34] = resolvedError;
    $[35] = showError;
    $[36] = validationIssues;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const renderError = t15;
  let t16;
  if ($[38] !== acceptedTypes) {
    t16 = acceptedTypes.join(",");
    $[38] = acceptedTypes;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== disabled || $[41] !== handleInputChange || $[42] !== t16) {
    t17 = <input accept={t16} className="hidden" data-testid="media-uploader-input" disabled={disabled} ref={fileInputRef} type="file" onChange={handleInputChange} />;
    $[40] = disabled;
    $[41] = handleInputChange;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const fileInput = t17;
  if (thumbnailUrl && !showError) {
    let t18;
    if ($[44] !== renderPreview) {
      t18 = renderPreview();
      $[44] = renderPreview;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] !== fileInput || $[47] !== t18) {
      t19 = <div className="flex flex-col gap-4">{t18}{fileInput}</div>;
      $[46] = fileInput;
      $[47] = t18;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    return t19;
  }
  let t18;
  if ($[49] !== renderError) {
    t18 = renderError();
    $[49] = renderError;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== renderDropzone) {
    t19 = renderDropzone();
    $[51] = renderDropzone;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== fileInput || $[54] !== t18 || $[55] !== t19) {
    t20 = <div className="flex flex-col gap-4">{t18}{t19}{fileInput}</div>;
    $[53] = fileInput;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  return t20;
}
function _temp2(issue) {
  return <li key={issue}><Text component="span" text={`\u2022 ${issue}`} tone="muted" variant="caption" /></li>;
}
function _temp(hint) {
  return <li className="flex list-none items-start gap-2" key={hint}><Text component="span" text={hint} tone="muted" variant="caption" /></li>;
}
export type { MediaUploaderProps, MediaUploaderStatus };
const DEFAULT_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/heic", "image/heif", "image/webp"];
const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024;
type MediaUploaderStatus = "idle" | "error";